import React, { useState, useEffect } from 'react';
import api from '../../utils/api/axiosInstance';
import sanitizeHtml from "../../utils/sanitizeHtml";
import PageSeo from '../../components/common/PageSeo';
import styles from '../../styles/pages/static/FAQPage.module.css';

const FAQPage = () => {
  const [faqs, setFaqs] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeItems, setActiveItems] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/faq');
      
      if (response.data.success) {
        setFaqs(response.data.faqs);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (id) => {
    setActiveItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleMarkHelpful = async (id) => {
    try {
      await api.post(`/faq/${id}/helpful`);
    } catch (error) {
      console.error('Error marking FAQ as helpful:', error);
    }
  };

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'general', label: 'General' },
    { id: 'account', label: 'Account' },
    { id: 'products', label: 'Products' },
    { id: 'orders', label: 'Orders' },
    { id: 'technical', label: 'Technical' },
  ];

  const getCategoryLabel = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat ? cat.label : catId;
  };

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : { [selectedCategory]: faqs[selectedCategory] || [] };

  return (
    <div className="pageContainer">
      <div className="innerContainer">
        <div className={styles.container}>
      <PageSeo
        title="Frequently Asked Questions"
        description="Find answers to common questions about Simpary - product comparison, recommendations, and more."
        pathname="/faq"
      />

      <div className={styles.header}>
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about Simpary</p>
      </div>

      <div className={styles.categoryFilter}>
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`${styles.categoryBtn} ${
              selectedCategory === cat.id ? styles.active : ''
            }`}
            onClick={() => setSelectedCategory(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.loading}>Loading FAQs...</div>
      ) : Object.keys(filteredFAQs).length === 0 ? (
        <div className={styles.noFaqs}>No FAQs available</div>
      ) : (
        <div className={styles.faqsContainer}>
          {Object.entries(filteredFAQs).map(([category, categoryFaqs]) => (
            <div key={category} className={styles.categorySection}>
              <h2 className={styles.categoryTitle}>{getCategoryLabel(category)}</h2>
              
              <div className={styles.faqList}>
                {categoryFaqs.map((faq) => (
                  <div key={faq._id} className={styles.faqItem}>
                    <button
                      className={`${styles.faqQuestion} ${
                        activeItems[faq._id] ? styles.active : ''
                      }`}
                      onClick={() => toggleFAQ(faq._id)}
                      aria-expanded={!!activeItems[faq._id]}
                    >
                      <span>{faq.question}</span>
                      <span className={styles.icon}>
                        {activeItems[faq._id] ? '−' : '+'}
                      </span>
                    </button>
                    
                    {activeItems[faq._id] && (
                      <div className={styles.faqAnswer}>
                        <div 
                          className={styles.answerContent}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.answer) }}
                        />
                        <div className={styles.faqActions}>
                          <button
                            className={styles.helpfulBtn}
                            onClick={() => handleMarkHelpful(faq._id)}
                          >
                            Was this helpful?
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.contactSection}>
        <h3>Still have questions?</h3>
        <p>Contact our support team for personalized assistance</p>
        <a href="/contact" className={styles.contactBtn}>
          Contact Support
        </a>
      </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
